<template>
  <div v-if="color" class="event-color-info">
    <BaseButton
      kind="auxiliary"
      type="secondary"
      text="Color"
      @click="openSettings"
    >
      <template #icon>
        <RTag
          class="swatch"
          text=""
          :theme="`calendar-${color}`"
          skin="dot"
          :has-padding="false"
        />
      </template>
      <template #right>
        <span
          ref="infoEl"
          class="info-icon"
          @mouseenter="showTooltip"
          @mouseleave="hideTooltip"
        >
          <InfoIcon />
        </span>
      </template>
    </BaseButton>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  ref,
  toRefs,
} from "vue";

import type { FullEvent, FullTask } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import RTag from "@/components/elements/RTag.vue";
import {
  colorFromSources,
  colorSourceLabels,
  fetchColorSourceCell,
  loadedColorSource,
} from "@/components/graph/calendarColorSource";
import { databaseForTable } from "@/components/graph/predicates";
import InfoIcon from "@/components/icons/InfoIcon.vue";
import * as inject from "@/components/inject";
import { EVENTS_TABLE_ID, TASKS_TABLE_ID } from "@/components/tables";
import { useTooltip } from "@/components/tooltip";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useCalendarColorSources } from "@/composables/useCalendarColorSources";
import { bindAsync, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";

/**
 * Read-only "Color" row of the event details: the color the event or
 * allocation gets from the Calendars Color setting, with a link to change
 * that setting.
 */
export default defineComponent({
  components: { BaseButton, InfoIcon, RTag },
  props: {
    event: { type: Object as PropType<FullEvent>, required: true },
    task: { type: Object as PropType<FullTask>, required: false },
  },
  async setup(props) {
    const { event, task } = toRefs(props);
    const controller = inject.controller();
    const profile = inject.profile();
    const types = inject.types();
    const calendars = inject.calendars();
    const router = inject.router();
    const releaser = useReleaser();
    const colorSources = releaser.add(useCalendarColorSources(profile));
    const browserStore = useBrowserStore(profile.value.user.accountId);
    const workspaceId = computed(() => profile.value.user.personal.workspaceId);

    const infoEl = ref<HTMLElement>();
    const tooltip = computed(() => {
      const isAllocation = task.value != null;
      const database = databaseForTable(
        types.value,
        isAllocation ? TASKS_TABLE_ID : EVENTS_TABLE_ID
      );
      const source = loadedColorSource(
        isAllocation ? colorSources.tasks.value : colorSources.events.value,
        types.value,
        database
      );
      const label = colorSourceLabels(types.value, database, source).join(
        " › "
      );
      return {
        text: `The color is computed from ${label}`,
        detail: "Click to configure",
      };
    });
    const {
      showTooltip,
      hideTooltip,
      release: releaseTooltip,
    } = useTooltip(infoEl, tooltip, "top");
    onBeforeUnmount(releaseTooltip);

    // Same fetches as the planner event fills: the source may live several
    // relation hops away, so the cell is fetched with a traversing query.
    const eventColorCellPromise = releaser.add(
      bindAsync(
        computed(() => ({
          rowId: event.value.extension_row,
          source: colorSources.events.value,
          types: types.value,
        })),
        ({ rowId, source, types: typesValue }) =>
          fetchColorSourceCell(
            controller,
            workspaceId.value,
            EVENTS_TABLE_ID,
            rowId,
            {
              source,
              types: typesValue,
              database: databaseForTable(typesValue, EVENTS_TABLE_ID),
            }
          )
      )
    );
    const taskColorCellPromise = releaser.add(
      bindAsync(
        computed(() => ({
          rowId: task.value?.extension_row,
          source: colorSources.tasks.value,
          types: types.value,
        })),
        ({ rowId, source, types: typesValue }) =>
          fetchColorSourceCell(
            controller,
            workspaceId.value,
            TASKS_TABLE_ID,
            rowId,
            {
              source,
              types: typesValue,
              database: databaseForTable(typesValue, TASKS_TABLE_ID),
            }
          )
      )
    );
    const [eventColorCell, taskColorCell] = await Promise.all([
      eventColorCellPromise,
      taskColorCellPromise,
    ]);

    const calendar = computed(
      () =>
        (event.value.calendar
          ? calendars.value?.byId.get(event.value.calendar)
          : undefined) ??
        calendars.value?.default ??
        null
    );
    const color = computed(() =>
      colorFromSources({
        isAllocation: task.value != null,
        types: types.value,
        sources: {
          events: colorSources.events.value,
          tasks: colorSources.tasks.value,
        },
        eventCell: eventColorCell.value,
        taskCell: taskColorCell.value,
        store: browserStore,
        calendar: calendar.value,
      })
    );

    const openSettings = () =>
      router.push({ name: RouteNames.SettingsCalendars });

    return {
      color,
      hideTooltip,
      infoEl,
      openSettings,
      showTooltip,
    };
  },
});
</script>

<style scoped lang="scss">
.event-color-info {
  display: flex;
  flex-direction: column;

  .swatch {
    display: flex;
    align-items: center;
  }

  .info-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: help;
    flex-shrink: 0;

    :deep(svg) {
      width: 14px;
      height: 14px;
    }
  }
}
</style>
